import { useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useGetAuthoringComponentQuery, useGetManifestQuery } from '../api/authoring'
import { useIntent } from '../api/useIntent'
import { useSelectedNode } from '../api/useSelectedNode'
import { problemMessage } from '../api/baseApi'
import { decodeLinkPath, encodeLinkPath, type LinkPath } from '../model/link'
import { flattenRoutes, resolveRouting } from '../model/website'
import { CompositionEditor } from '../ui/CompositionEditor'
import { NodePanel } from '../ui/NodePanel'
import { Loading, QueryError } from '../ui/QueryState'
import { PageHeader } from './page/PageHeader'

/**
 * Editing a page.
 *
 * The composition on the left, the selected node's editor on the right.
 * **Selection is a link path**, held in the URL as `?sel=b1.g1` so it is
 * linkable and survives a reload — a component id is what the editor
 * loads, a link path is what the author selected, and the two are not
 * interchangeable the moment one component is linked twice.
 *
 * **This is a face of the page, not a mode of the client.** It was
 * `/:tenant/build/:site/pages/:id` and it is `…/pages/:id/structure`,
 * and the difference is more than an address: an owner who needs one
 * thing the model knows — a link id to paste, whether a block is pinned,
 * which placement is linked rather than inline — reaches it from the tab
 * strip on the page they are already reading, and comes back the same
 * way with their selection intact.
 *
 * It draws no preview and no settings of its own, because the page
 * already has both and they are two tabs away. Two screens rendering one
 * frame was what the mode cost: the same page, previewed twice, at two
 * addresses.
 *
 * Nothing here renders the page itself. Placements are a list in slot
 * order.
 *
 * A page's own fields are the panel's when nothing is selected, and the
 * split between what a Page owns and what the Website owns is the point
 * of this face rather than a detail to be smoothed over — Settings is
 * where the two are shown as one form.
 */
export function PageEditor() {
  const { tenant = '', site = '', pageId = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const selection = decodeLinkPath(params.get('sel'))

  const manifest = useGetManifestQuery({ tenant })
  const page = useGetAuthoringComponentQuery({ tenant, id: pageId })
  // The site, for one thing only: where this page answers. It is the
  // same document the site editor holds, and it is already in cache
  // whenever the author arrived through one.
  const website = useGetAuthoringComponentQuery({ tenant, id: site })
  const { send, isLoading: sending, error: sendError } = useIntent(tenant)

  const select = useCallback(
    (path: LinkPath) => {
      const next = new URLSearchParams(params)
      if (path.length === 0) next.delete('sel')
      else next.set('sel', encodeLinkPath(path))
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  // What the selection resolves to, and where an intent editing its own
  // fields goes: the page itself for an inline node, the target's own
  // component once the path crosses a link. Shared with the component
  // editor, because the rule is the model's rather than this screen's.
  const selected = useSelectedNode(tenant, page.data, selection)

  if (manifest.isLoading || page.isLoading) return <Loading label="Reading the page…" />
  if (page.error) return <QueryError error={page.error} title="Could not read this page" />
  if (!page.data) return null

  // Where this page answers, for the publication chip beside its name.
  const urlPath = website.data
    ? flattenRoutes(resolveRouting(website.data)).find((route) => route.targetId === pageId)?.urlPath
    : undefined

  return (
    <Stack spacing={3}>
      <PageHeader
        tenant={tenant}
        site={site}
        pageId={pageId}
        page={page.data}
        website={website.data}
        manifest={manifest.data}
        face="structure"
        params={params}
        urlPath={urlPath}
        sending={sending}
      />

      {sendError && <Alert severity="error">{problemMessage(sendError, 'The intent was refused.')}</Alert>}

      {
        /* The panel grows with the window, and has the larger share of
           it: it holds a rich-text editor and a generated form, which is
           where the author works, while the tree beside it is a list of
           names. It was once a flat 420px, and then capped at 720px,
           and both held the editor to a column with room beside it. */
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(360px, 3fr)' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          <Box>
            <CompositionEditor
              tenant={tenant}
              document={page.data}
              manifest={manifest.data}
              selection={selection}
              onSelect={select}
              send={send}
              busy={sending}
            />
          </Box>

          {/* The selected node's editor. It doubles as the page's own
              form when nothing is selected: a Page's fields are its
              title and its search metadata, and the split between what
              a Page owns and what the Website owns is the point of this
              face rather than a detail to be smoothed over. That is why
              this face has no settings tab of its own — Settings is
              where the two are shown as one form. */}
          <NodePanel
            tenant={tenant}
            manifest={manifest.data}
            selection={selection}
            selected={selected}
            document={page.data}
            onSelect={select}
            send={send}
            busy={sending}
            editsRoot
            empty={
              <Typography variant="caption" color="text.secondary">
                The page&rsquo;s own fields. Select a placement to edit it.
              </Typography>
            }
          />
        </Box>
      }
    </Stack>
  )
}
